<template>
  <div>
    <top-buttons-component
      v-on:backToJobs="$emit('backToJobs', 'Jobs')"
    />

    <div class="job-detail-container" v-if="job" :class="{'no-cover-photo-padding': !job.jobCoverPhotoUrl}">

      <div class="job-detail-content-one" v-if="!loading">
        <!-- Job Cover Photo -->
        <div v-if="job.jobCoverPhotoUrl" class="jobcoverPhoto-holder" >
          <img v-if="job.jobCoverPhotoUrl" :src="job.jobCoverPhotoUrl" alt="Job cover photo" />
          <img v-else src="../../assets/images/image-placeholder.png" alt="Placeholder image" />
        </div>

        <div class="first-name-cases">
          <p>{{ jobCompanyInitials }}</p>
        </div>

        <!-- Job Company Name and Location -->
        <div class="name-and-location">
          <p class="name">{{ job.companyName }}</p>
          <p class="location">
            <img src="../../assets/images/location-logo.png" alt="Location icon" />
            {{ `${job.city}, ${job.state}` }}
          </p>
        </div>

        <!-- Job Title and Date -->
        <div class="title-and-date" :class="{addBorderBottom: !appliedToJob}">
          <p class="title">{{ job.title }} | {{ job.employmentType }}</p>
          <p class="title">
            <span class="date">{{ job.jobDeadline  ? 'Deadline:' : 'Posted:'}}</span>
            {{ job.jobDeadline ? new Date(job.jobDeadline + 'T00:00:00').toDateString() : new Date(job.createdAt).toDateString() }}
          </p>
        </div>

        <!-- Job Application Status Notification -->
        <div class="title-date applied-status" v-if="appliedToJob">
          <div class="status-notice">
            <span class="info-img"><img src="../../assets/images/info-help.svg" alt="Info icon"/></span>
            You have applied to this job.
          </div>
          <div class="status-label">
            Status:
            <span class="status-text" :class="[jobAppliedStatus[applicationToJob.status]]">{{applicationToJob && applicationToJob.status}}</span>
          </div>
          <!-- Display decision comment based on application status -->
          <p v-if="applicationToJob && applicationToJob.status == 'in-review'" class="decision-comment">
            <span><b>Application Update:</b><br></span>
            Your application is currently being reviewed by our hiring team. We appreciate your patience during this process.
            We will be in touch with you as soon as we have an update regarding the status of your application.
          </p>

          <p v-if="applicationToJob && applicationToJob.status == 'shortlisted'" class="decision-comment">
            <span><b>Application Update:</b><br></span>
            Congratulations! Your application has been shortlisted.
            Our hiring team will be reviewing your application in more detail and will be in touch with you regarding the next steps in the hiring process.
            We appreciate your interest in joining our team and look forward to connecting with you soon.
          </p>

          <p v-if="applicationToJob && applicationToJob.status == 'declined'" class="decision-comment">
            <span><b>Decision Note:</b><br></span>
            Thank you for taking the time to apply and for your interest in joining our team. After careful consideration,
            we have decided not to move forward with your application for this role at this time.
            We appreciate your interest in our company and encourage you to explore future opportunities
            that may be a better match for your skills and experience. We wish you all the best in your job search.
          </p>

          <p v-if="applicationToJob && applicationToJob.status == 'scheduled-for-interview'" class="decision-comment">
            <span v-if="applicationToJob.interviewTitle"><b>{{ applicationToJob.interviewTitle }}</b><br></span>
            <span v-if="applicationToJob.interviewDate">Your interview is scheduled for {{ new Date(applicationToJob.interviewDate).toDateString() }}</span>
            <span v-if="applicationToJob.interviewTime"> at {{ applicationToJob.interviewTime }}</span>.
            <span v-if="applicationToJob.interviewLink"> Join the interview <b><a :href="applicationToJob.interviewLink" target="_blank">here</a></b>.</span>
            <br><br/>
            <span v-if="applicationToJob.interviewComment"> Employer's comment:<br> "{{ applicationToJob.interviewComment }}"</span>
          </p>

          <p v-if="applicationToJob && applicationToJob.status == 'hired'" class="decision-comment">
            <span><b>Decision Note:</b><br></span>
            We are pleased to offer you the position of {{ job.title }} at {{ job.companyName }}.
            Please review the offer details that have been sent to you and let us know if you have any questions.
            We look forward to welcoming you to the team.
          </p>
        </div>

        <!-- Job Overview -->
        <div class="overview">
          <h3>Overview</h3>
          <p>{{ job.jobDescription }}</p>
        </div>

      </div>

      <!-- Job Details-->
      <div class="job-detail-content-two" v-if="!loading">
        <div class="combined-title-text">
          <!-- Job Contact Email -->
          <div class="holder">
            <p class="title">Email Address</p>
            <p class="text">
              <span v-if="job.companyContactEmail">
                {{ job.companyContactEmail }}
              </span>
              <span v-if="!job.companyContactEmail">—</span>
            </p>
          </div>

          <!-- Job Contact Number -->
          <div class="second-holder">
            <p class="title">Contact Number</p>
            <p class="text">
              <span v-if="job.companyContactNumber">
                {{ job.companyContactNumber }}
              </span>
              <span v-if="!job.companyContactNumber">—</span>
            </p>
          </div>
        </div>

        <!-- Job Company Contact Location -->
        <div class="combined-title-text">
          <div class="holder">
            <p class="title">Address</p>
            <p class="text">
              <span v-if="job.city && job.state">
                {{ `${job.city}, ${job.state}` }}
              </span>
              <span v-else>—</span>
            </p>
          </div>

          <!-- Job Company Website -->
          <div class="second-holder">
            <p class="title">Website URL</p>
            <p class="text">
              <span v-if="job.companyWebsiteUrl">
                <a :href="job.companyWebsiteUrl" target="_blank">{{ job.companyWebsiteUrl }}</a>
              </span>
              <span v-else>—</span>
            </p>
          </div>
        </div>

        <!-- Job Contact Person Name -->
        <div class="combined-title-text">
          <div class="holder">
            <p class="title">Contact Person</p>
            <p class="text">
              <span v-if="job.companyContactPerson">
                {{ job.companyContactPerson }}
              </span>
              <span v-else>—</span>
            </p>
          </div>

          <!-- Job Contact Person Phone -->
          <div class="second-holder">
            <p class="title">Contact Person Number</p>
            <p class="text">
              <span v-if="job.companyContactNumber">
                {{ job.companyContactNumber }}
              </span>
              <span v-else>—</span>
            </p>
          </div>
        </div>

        <!-- Job Questions -->
        <div class="second-title" v-if="job.applicantQuestionOne">
          <h3>{{ job.applicantQuestionOne }} <span class="required">*</span></h3>
          <select
            name="firstResponse"
            v-model="formData.firstResponse"
            v-on:change="validateInput"
            required
            v-if="!appliedToJob"
          >
            <option
              v-for="(item, index) in optionsList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-else class="text q-and-a">
            <span class="span-ref">Your Response:</span>
            {{ applicationToJob.firstResponse }}
          </p>
          <p v-if="error.firstResponseError" class="error-text">
            Please select an option
          </p>
        </div>

        <div class="second-title" v-if="job.applicantQuestionTwo">
          <h3>{{ job.applicantQuestionTwo }} <span class="required">*</span></h3>
          <select
            name="secondResponse"
            v-model="formData.secondResponse"
            v-on:change="validateInput"
            required
            v-if="!appliedToJob"
          >
            <option
              v-for="(item, index) in optionsList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-else class="text q-and-a">
            <span class="span-ref">Your Response:</span>
            {{ applicationToJob.secondResponse }}
          </p>
          <p v-if="error.secondResponseError" class="error-text">
            Please select an option
          </p>
        </div>

        <div class="second-title" v-if="job.applicantQuestionThree">
          <h3>{{ job.applicantQuestionThree }} <span class="required">*</span></h3>
          <select
            name="thirdResponse"
            v-model="formData.thirdResponse"
            v-on:change="validateInput"
            required
            v-if="!appliedToJob"
          >
            <option
              v-for="(item, index) in optionsList"
              :key="index"
              :value="item.value"
            >
              {{ item.title }}
            </option>
          </select>
          <p v-else class="text q-and-a">
            <span class="span-ref">Your Response:</span>
            {{ applicationToJob.thirdResponse }}
          </p>
          <p v-if="error.thirdResponseError" class="error-text">
            Please select an option
          </p>
        </div>

        <!-- Curriculum Vitae Upload Section-->
        <div
          v-if="!appliedToJob"
          class="second-title">
          <h3>
            Attach your CV or a single document of your CV &amp; academic certificate
            <span class="file-size">(max 3MB - .pdf, .doc, .docx)</span>
          </h3>
          <div class="upload-file">
            <div class="fit-object" v-if="!cvName"
              @click="$refs.myFile.click()"
            >
              <img src="../../assets/images/attachment-logo.svg" alt="attachment icon" />
              <p>Attach File</p>
              <input
              type="file"
              ref="myFile"
              @change="selectedcvFile"
              hidden
            />
            </div>
            <div v-if="cvName">
              <img src="../../assets/images/attachment-logo.svg" alt="attachment icon" />
              <p>Selected File: {{cvName}}</p>
              <button @click="resetcvData">Remove File</button>
            </div>
          </div>
        </div>

        <!-- Video CV Questions or Guideline-->
        <div v-if="job.videoCvGuide" class="second-title">
          <h3>Video Questions (Guide)</h3>
          <p class="video-hint">{{ job.videoCvGuide }}</p>

          <!-- View uploaded video when candidate already submitted an application -->
          <button v-if="appliedToJob" class="video-wrapper" @click="showVideoModal=true">
            View Video
          </button>

        </div>

        <!-- Video CV Upload Section -->
        <div
          v-if="!appliedToJob && job.videoCvGuide"
          class="second-title"
        >
          <h3>
            Attach your video file <span class="file-size">(max 100MB - .mp4)</span>
          </h3>
          <div class="upload-file">
            <div class="fit-object" v-if="!videoName"
              @click="$refs.myVideoFile.click()"
            >
              <img src="../../assets/images/attachment-logo.svg" alt="attachment icon" />
              <p>Attach File</p>
              <input
                type="file"
                placeholder="Upload CoverImage"
                ref="myVideoFile"
                @change="selectedVideoFile"
                hidden
              />
            </div>
            <div v-if="videoName">
              <img src="../../assets/images/attachment-logo.svg" alt="attachment icon" />
              <p>Selected File: {{videoName}}</p>
              <button @click="resetVideoData">Remove File</button>
            </div>
          </div>
          <h3 style="margin-top: 35px;">OR</h3>
        </div>

        <!-- Previously uploaded documents and files -->
        <div class="second-title" v-if="!appliedToJob">
          <h3>
            Select a Previously Uploaded CV
          </h3>
          <select
            name="previousCV"
            v-model="formData.selectedCV"
            @change="validateInput"
          >
            <option
              v-for="(item, index) in applicantCVList"
              :key="index"
              :value="item.secureUrl"
            >
            <div class="file-option" v-if="item.name && item.format">
                {{`${item.name}_cv.${item.format} - ${new Date(item.createdAt).toDateString()}`}}
            </div>
            <div v-else>
              {{`${getUserFullname}_${index}_cv.pdf - ${new Date(item.createdAt).toDateString()}`}}
            </div>
            </option>
          </select>
        </div>

        <div class="second-title" v-if="!appliedToJob && job.videoCvGuide">
          <h3>
            Select a Previously Uploaded Video File
          </h3>
          <select
            name="previousVideo"
            v-model="formData.selectedVideo"
            @change="validateInput"
          >
            <option
              v-for="(item, index) in applicantVideoList"
              :key="index"
              :value="item.secureUrl"
            >
              <div class="file-option" v-if="item.name && item.format">
                {{`${item.name}_video.${item.format} - ${new Date(item.createdAt).toDateString()}`}}
            </div>
            <div v-else>
              {{`${getUserFullname}_${index}_video.mp4 - ${new Date(item.createdAt).toDateString()}`}}
            </div>
            </option>
          </select>
        </div>
        <div v-if="!appliedToJob">
          <p
            class="error-text"
            v-if="error.selectedCVError"
          >
            Upload a cv no more than the specified file size and type, or select from a previously uploaded file.
          </p>
          <p
            class="error-text"
            v-if="error.selectedVideoError"
          >
            Upload a video no more than the specified file size and type, or select from a previously uploaded file.
          </p>
        </div>
        <div class="action-buttons-holder" v-if="!appliedToJob">
          <button class="cancel-button" @click="cancelButton">Cancel</button>
          <button class="apply-button" @click="handleSubmit">Apply</button>
        </div>
      </div>
    </div>


    <!-- Modal component for actions confirmation -->
    <div v-if="showModal">
      <ActionModal
        :verb="'cancel'"
        :subject="'application'"
        v-on:modalAction="modalAction"
      />
    </div>


    <!-- Modal component for Successful Job Post -->
    <div v-if="showSuccessModal">
      <SuccessModal
        v-on:closeSuccessModal="showSuccessModal = false"
        v-bind:successTitle="'Success!'"
        v-bind:successImage="successImage"
        v-bind:successMessage="'Application has successfully been submitted'"
      />
    </div>


    <!-- Video popup modal -->
    <div v-if="showVideoModal">
      <VideoModal
        v-on:closeVideoModal="showVideoModal = false"
        :videoUrl="applicationToJob.videoUrl"
        :applicantName="applicantFullName"
      />
    </div>

    <!-- Loader Component -->
    <Loader v-if="loading" />

  </div>
</template>

<script>
import TopButtonsComponent from '../../components/JobDetail/TopButtonsComponent.vue';
import Loader from '../../components/body/Loader.vue';
import ActionModal from '../../components/Modals/ActionModal.vue';
import SuccessModal from '../../components/Modals/SuccessModal.vue';
import VideoModal from '../../components/Modals/VideoModal.vue';
import { textToInitials } from '../../utils/initials';

import successImage from '../../assets/images/success-logo.png';

export default {
  name: 'ApplicantJobDetail',
  props: ['jobId'],
  components: {
    TopButtonsComponent,
    Loader,
    ActionModal,
    SuccessModal,
    VideoModal
  },
  data() {
    return {
      loading: false,
      id: this.jobId,
      job: null,
      appliedToJob: false,
      applicantCVList: null,
      applicantVideoList: null,
      formData: {
        firstResponse: null,
        secondResponse: null,
        thirdResponse: null,
        selectedCV: null,
        selectedVideo: null,
      },
      error: {
        firstResponseError: false,
        secondResponseError: false,
        thirdResponseError: false,
        selectedVideoError: false,
        selectedCVError: false
      },
      cvUploadError:
      'We accept only pdf, doc, docx files of not more than 3MB. Alternatively, select from previously uploaded CVs.',
      videoUploadError:
      'We accept only mp4 videos of not more than 100MB. Alternatively, select from previously uploaded video files.',
      successImage: null,
      optionsList: [
        { title: 'Yes', value: 'yes' },
        { title: 'No', value: 'no' },
      ],
      videoName: null,
      cvName: null,
      showModal: false,
      showSuccessModal: false,
      applicationToJob: null,

      jobAppliedStatus: {
        'in-review': 'in-review',
        'shortlisted': 'shortlisted',
        'scheduled-for-interview': 'interviewing',
        'hired': 'hired',
        'declined': 'declined'
      },      
      showVideoModal: false,
    };
  },
  computed: {
    jobCompanyInitials() {
      return textToInitials(this.job?.companyName || '');
    },
    getUserFullname(){
      return `${this.$store.state.userData.firstName} ${this.$store.state.userData.lastName}`;
    },
    applicantFullName() {
      if (this.applicationToJob?.applicantInfo?.firstName && this.applicationToJob?.applicantInfo?.lastName) {
        return `${this.applicationToJob.applicantInfo.firstName} ${this.applicationToJob.applicantInfo.lastName}`;
      } else if (this.applicationToJob?.applicantInfo?.firstName) {
        return this.applicationToJob.applicantInfo.firstName;
      } else if (this.applicationToJob?.applicantInfo?.lastName) {
        return this.applicationToJob.applicantInfo.lastName;
      } else {
        return 'Applicant';
      }
    }
  },
  async beforeMount() {
    await this.getJobById();
  },
  methods: {
    async getJobById() {

      this.loading = true;

      await this.$store
        .dispatch('fetchJob', this.$route.params.id)
        .then((res) => {
          if (res.response) {
            this.applicantCVList = [];
            this.applicantVideoList = [];

            this.job = res.response.data;

            // Check if user already applied
            this.applicationToJob = this.$store.state.userData.myApplications.find((application) => {
              return application.jobId === this.$route.params.id;
            });
            this.applicationToJob ?
            this.appliedToJob = true : null;

            this.applicantCVList = this.$store.state.userData.userCVList.reverse();
            this.applicantVideoList = this.$store.state.userData.userVideoList.reverse();

            this.loading = false;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$toastr.error(
            err.error.error
              ? err.error.error
              :err.error
                ? err.error.errorMessage
                :err.error,
            'Failed'
          );
        });
    },
    selectedcvFile(event) {
      if ((event.target.files && event.target.files[0].type === 'application/pdf' ||
        event && event.target.files[0].type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') &&
         event.target.files[0].size <= 3000000
      ) {
        this.formData.selectedCV = event.target.files[0];
        this.cvName = event.target.files[0].name;
        this.error.selectedCVError = false;
      } else {
        this.error.selectedCVError = true;
        this.$toastr.error(this.cvUploadError, 'Wrong File Type/Size:');
      }
    },
    selectedVideoFile(event) {
      if (
        event.target.files &&
        event.target.files[0].type === 'video/mp4' &&
        event.target.files[0].size <= 100000000
      ) {
        this.formData.selectedVideo = event.target.files[0];
        this.videoName = event.target.files[0].name;
        this.error.selectedVideoError = false;
      } else {
        this.error.selectedVideoError = true;
        this.$toastr.error(this.videoUploadError, 'Wrong File Type/Size:');
      }
    },
    resetVideoData() {
      this.videoName = null;
      this.formData.selectedVideo = null;
    },
    resetcvData() {
      this.cvName = null;
      this.formData.selectedCV = null;
    },
    cancelButton(){
      this.showModal = !this.showModal;
    },
    handleSubmit(){
      if(
        (this.job.applicantQuestionOne && !this.formData.firstResponse) ||
        (this.job.applicantQuestionTwo && !this.formData.secondResponse) ||
        (this.job.applicantQuestionThree && !this.formData.thirdResponse) ||
        this.error.firstResponseError ||
        this.error.secondResponseError ||
        this.error.thirdResponseError ||
        !this.formData.selectedCV ||
        this.error.selectedCVError ||
        (this.job.videoCvGuide && !this.formData.selectedVideo) ||
        this.error.selectedVideoError
      ){
        Object.keys(this.formData).forEach((field) => {
          if (!this.formData[field] && field ==='firstResponse' && this.job.applicantQuestionOne!=='') {
            this.error[`${field}Error`] = true;
          }
          if (!this.formData[field] && field ==='secondResponse' && this.job.applicantQuestionTwo!=='') {
            this.error[`${field}Error`] = true;
          }
          if(!this.formData[field] && field ==='thirdResponse' && this.job.applicantQuestionThree!=='') {
            this.error[`${field}Error`] = true;
          }
          if(!this.formData[field] && field ==='selectedCV') {
            this.error[`${field}Error`] = true;
          }
          if(!this.formData[field] && field ==='selectedVideo' && this.job.videoCvGuide !=='') {
            this.error[`${field}Error`] = true;
          }
        });

      } else {
        this.loading = true;
        this.$store.dispatch('applyToJob', [this.formData, this.$route.params.id])
        .then((res) => {
          if(res.response){
            this.successImage = successImage;
            this.showSuccessModal = true;
            this.loading = false;

            Object.keys(this.formData).forEach((field) => {
              this.formData[field] = null;
              this.cvName = null;
              this.videoName = null;
            });
            // Reload page data to show real-time data
            setTimeout(() => {
              this.getJobById();
              this.showSuccessModal = false;
            }, 3000);
          }
          if(res.error) {
            this.loading = false;

            Object.keys(this.formData).forEach((field) => {
              this.formData[field] = null;
              this.cvName = null;
              this.videoName = null;
            });

            this.$toastr.error(
              res.error.error
                ? res.error.error
                : res.error
                  ? res.error.errorMessage
                  : res.error,
              'Failed'
            );
          }
        });
      }
    },
    modalAction(action) {
      this.showModal = !this.showModal;
      action === 'yes'?
      this.$emit('backToJobs', 'Jobs') :
      null;
    },
    validateInput(e) {
      let fieldName = e.target.name;
      let fieldValue = e.target.value;

      switch (fieldName) {
        case 'firstResponse':
          if(!fieldValue) {
            this.error.firstResponseError = true;
          } else {
            this.error.firstResponseError = false;
          }
          break;

        case 'secondResponse':
          if(!fieldValue) {
            this.error.secondResponseError = true;
          } else {
            this.error.secondResponseError = false;
          }
          break;

        case 'thirdResponse':
          if(!fieldValue) {
            this.error.thirdResponseError = true;
          } else {
            this.error.thirdResponseError = false;
          }
          break;

        case 'previousCV':
          this.cvName = null;
          if(!fieldValue && (this.cvName === null || this.cvName === undefined)) {
            this.error.selectedCVError = true;
          } else {
            this.error.selectedCVError = false;
          }
          break;

        case 'previousVideo':
          this.videoName = null;
          if(!fieldValue && (this.videoName === null || this.videoName === undefined)) {
            this.error.selectedVideoError = true;
          } else {
            this.error.selectedVideoError = false;
          }
          break;

        default:
          null;
          break;
      }
    },
  },
};
</script>

<style scoped>
@keyframes buttonLoadingSpinner {
    from {
      transform: rotate(0turn);
    }
    to {
      transform: rotate(1turn);
    }
  }

  .loading-text {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 50px;
    color: white;
    justify-content: center;
  }
  .loading::after {
    content: "";
    position: absolute;
    width: 100px;
    height: 100px;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    border: 4px solid transparent;
    border-top-color: var(--white);
    border-right-color: var(--white);
    border-radius: 50%;
    animation: buttonLoadingSpinner 1s linear infinite;
  }

.job-detail-container {
  background: var(--white);
  border: 0.5px solid rgba(201, 201, 201, 0.35);
  box-sizing: border-box;
  box-shadow: 0px 14px 24px rgba(9, 44, 76, 0.1);
  border-radius: 10px 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
}
.no-cover-photo-padding {
  padding-top: 2rem;
}
.jobcoverPhoto-holder {
  position: relative;
}
.job-detail-content-one{
  width: 50%;
}
.job-detail-content-two {
  width: 48%;
  margin-top: 80px;
}
.first-name-cases {
  position: relative;
  background: var(--colorPrimary);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 30px;
  top: -60px;
}
.first-name-cases p {
  font-family: 'montserrat';
  font-style: normal;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 1.688rem;
  line-height: 2.057rem;
  color: var(--white);
}
.name-and-location,
.title-and-date {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-left: 30px;
}
.title-and-date {
  flex-direction: column;
  margin-top: 30px;
  padding-bottom: 30px;
}
.addBorderBottom {
  border-bottom: 1px solid #dddddd;
}
.applied-status {
  padding: 0 30px 30px 30px;
  border-bottom: 1px solid #dddddd;
}
.status-notice {
  margin-bottom: 10px;
  color: orange;
  font-weight: bold;
}
span.info-img {
  padding-right: 3px;
}
.info-img img {
  width: 15px;
  height: 15px;
}
.status-label {
  font-weight: bold;
}
span.status-text {
  padding: 20px 0 20px 5px;
  text-transform: capitalize;
}
.in-review, .interviewing {
  color: var(--colorSecondary);
}
.shortlisted {
  color: #FFB300;
}
.hired {
  color: #27AE60;
}
.declined {
  color: #DF2029;
}
.name-and-location .name {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1.188rem;
  line-height: 1.829rem;
}
.name-and-location .location {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.438rem;
  color: var(--colorSecondary);
  text-transform: capitalize;
}
.title-and-date .title {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 2.057rem;
  color: var(--colorSecondary);
  text-transform: capitalize;
}
.title-and-date .date {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.444rem;
  color: #828282;
}
.jobcoverPhoto-holder img {
  max-height: 300px;
  width: 100%;
  filter: drop-shadow(5px 4px 18px rgba(180, 180, 180, 0.15));
  border-radius: 15px 15px 0px 0px;
  object-fit: cover;
}
.overview {
  padding: 0 30px;
  margin-top: 30px;
  margin-bottom: 30px;
  white-space: pre-wrap;
}
.overview h3 {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.6rem;
  color: var(--colorSecondary);
  margin-bottom: 20px;
}
.overview p {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 1rem;
  line-height: 2.313rem;
  text-align: justify;
  color: #8c8c8c;
  white-space: pre-wrap;
}
.combined-title-text {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 65px;
}
.combined-title-text .holder {
  width: 50%;
}
.combined-title-text .second-holder {
  width: 50%;
}
.combined-title-text .title {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.989rem;
  color: #707070;
  margin-right: 50px;
  margin-bottom: 14px;
}
.combined-title-text .text {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1.809rem;
  color: var(--colorSecondary);
}
.second-title {
  margin-bottom: 50px;
}
.second-title h3 {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.628rem;
  color: var(--colorSecondary);
  margin-bottom: 13px;
}
select {
  width: 100%;
  max-width: 85%;
  height: 45px;
  box-shadow: 0px 0px 4px 2px rgba(39, 31, 31, 0.15);
  border-radius: 5px;
  outline: none;
  padding: 10px;
  font-family: 'montserrat';
  font-size: 1rem;
  color: rgba(51, 52, 54, 0.7);
  line-height: 1.25rem;
  /* identical to box height */
  -webkit-appearance: auto;
  color: rgba(51, 52, 54, 0.7);
}
.required, .error-text {
  color: tomato;
}
.error-text {
  margin-top: 12px;
  font-size: 12px;
}
p.video-hint {
  line-height: 1.86;
  padding-right: 5px;
}
.upload-file {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    background: var(--white);
    border: 1px dashed #DBDBDB;
    box-sizing: border-box;
    border-radius: 5px;
    padding: 20px;
    cursor: pointer;
    height: 150px;
    max-height: 150px;
    width: 85%;
  }
  .fit-object {
    width: 100%;
  }
  .upload-file p {
    font-family: 'montserrat';
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #606060;
    margin-top: 16px;
  }
  .file-size {
    font-size: 12px;
  }
  .action-buttons-holder {
    display: flex;
    justify-content: right;
    width: 85%;
    margin-top: 20px;
    margin-bottom: 80px;
  }
  .action-buttons-holder .apply-button {
    background: var(--colorPrimary);
    font-size: 1.125rem;
    font-weight: 600;
    border: none;
    color: var(--white);
    border-radius: 5px;
    height: 53px;
    width: 188px;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15));
  }
  .action-buttons-holder .cancel-button {
    background: var(--white);
    height: 53px;
    width: 188px;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15));
    border-radius: 5px;
    font-size: 1.125rem;
    font-weight: 600;
    margin-right: 30px;
  }
  .q-and-a {
    margin-top: 10px;
    padding: 20px;
    background: #f5f5f5;
    border-radius: 5px;
    text-transform: capitalize;
    width: 93%;
    font-size: 0.85rem;
  }
  .video-wrapper {
    margin-top: 1rem;
    color: var(--white);
    font-size: 0.85rem;
    font-weight: bold;
    padding: 1rem 1.25rem;
    background: var(--colorPrimary);
    border-radius: 5px;
    text-transform: capitalize;
  }
  .decision-comment {
    margin: 1.65rem 0 0;
    font-size: 0.85rem;
    line-height: 1.4rem;
    text-align: justify;
    background-color: rgb(249, 245, 239);
    padding: 1.25rem;
  }

  /* Responsive Designs */
  @media only screen and (min-width: 1201px) {
    .job-detail-content-two {
      padding-left: 45px;
    }
    .jobcoverPhoto-holder img {
      border-radius: 15px 0px 0px 0px;
    }
    .second-title h3 {
      width: 93%;
    }
  }
  @media only screen and (max-width: 1200px) {
    .job-detail-container {
      flex-direction: column;
    }
    .job-detail-content-one, .job-detail-content-two {
      width: 100%;
    }
    .name-and-location, .title-and-date {
      padding-left: 30px;
      padding-right: 30px;
    }
    .job-detail-content-two {
      padding: 0 30px;
    }
    .combined-title-text .second-holder, .combined-title-text .holder {
      width: 100%;
    }
    select {
      max-width: 100%;
    }
    .upload-file, .action-buttons-holder {
      width: 100%;
    }
    .q-and-a {
      width: 100%;
    }
  }
  @media only screen and (max-width: 767px) {
    .name-and-location .location,
    .title-and-date .date,
    .title-and-date .title {
      font-size: 1rem;
    }
    .overview p {
      text-align: left;
    }
  }
</style>
